import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useShop } from "@/context/ShopContext";
import { api, img, money } from "@/lib/api";
import useSeo from "@/lib/useSeo";

export default function PaymentSuccess() {
  useSeo("Order confirmed");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useShop();
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          const o = await api.get(`/orders/by-session/${sessionId}`);
          setOrder(o.data); setState("paid"); clear(); return;
        }
        if (data.status === "expired") { setState("error"); return; }
      } catch { /* retry */ }
      if (++tries >= 10) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (state !== "paid") return (
    <div data-testid="payment-status-page" className="px-6 sm:px-16 py-32">
      <p className="eyebrow text-[var(--mm-red)] mb-4">Payment</p>
      <p data-testid="payment-status-text" className="font-serif-mm text-5xl">
        {state === "checking" ? <>Confirming your <em>payment…</em></> : state === "timeout" ? "We're still confirming your payment. Please check back shortly." : "We couldn't confirm this payment."}
      </p>
      {state !== "checking" && <Link to="/shop" className="btn-mm mt-10">Return to shop</Link>}
    </div>
  );

  return (
    <div data-testid="order-confirmation-page" className="grid lg:grid-cols-12 gap-12 px-5 sm:px-10 lg:px-16 py-20">
      <div className="lg:col-span-6">
        <p className="eyebrow text-[var(--mm-red)] mb-4 rise">Order confirmed</p>
        <h1 className="font-serif-mm text-5xl sm:text-6xl leading-none rise d1">Thank you, <em className="text-[var(--mm-red)]">{order.customer.first_name}.</em></h1>
        <p className="mt-6 text-sm text-[var(--mm-muted)] max-w-md rise d2">Your order has been received and is being prepared with care. Keep your order number for reference.</p>
        <p className="mt-8 eyebrow">Order number</p>
        <p data-testid="order-number" className="font-serif-mm text-4xl mt-1">{order.order_number}</p>
        <div className="mt-8 text-sm leading-relaxed">
          <p className="eyebrow mb-2">Delivering to</p>
          <p>{order.customer.first_name} {order.customer.last_name}<br />{order.customer.address1}{order.customer.address2 ? `, ${order.customer.address2}` : ""}<br />{order.customer.city}, {order.customer.postcode}<br />{order.customer.country}</p>
        </div>
        <Link to="/shop" data-testid="continue-shopping-button" className="btn-mm mt-10">Continue shopping</Link>
      </div>
      <div className="lg:col-span-5 lg:col-start-8 bg-[var(--mm-sand)] p-8">
        <p className="eyebrow mb-6">Summary</p>
        {order.items.map((it, i) => (
          <div key={i} className="flex gap-4 mb-4">
            <img src={img(it.image)} alt="" className="w-16 aspect-[3/4] object-cover" />
            <div className="flex-1 text-sm"><p className="font-semibold">{it.name}</p><p className="text-xs text-[var(--mm-muted)]">Size {it.size} · Qty {it.quantity}</p></div>
            <p className="text-sm">{money(it.price * it.quantity)}</p>
          </div>
        ))}
        <div className="border-t border-[var(--mm-line)] pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span>Subtotal</span><span>{money(order.subtotal)}</span></div>
          <div className="flex justify-between"><span>Delivery</span><span>{order.shipping ? money(order.shipping) : "Complimentary"}</span></div>
          <div className="flex justify-between font-semibold text-base"><span>Total paid</span><span data-testid="order-total">{money(order.total)}</span></div>
        </div>
      </div>
    </div>
  );
}
